<template>
  <div class="w-full">
    <div
      class="flex flex-col items-start justify-between bg-white sm:flex-row [&>*]:w-full md:[&>*]:w-max"
    >
      <div
        class="flex w-full min-w-max gap-6 border-b border-steel-300 bg-white px-3 pb-4 md:w-max md:border-none"
        :class="{
          'mb-6 pl-4': isDesktop,
          'mb-0 bg-white pb-6 pl-4': !isDesktop,
        }"
      >
        <span
          class="cursor-pointer"
          :class="{
            'border-b-2 border-sun-700 text-sun-700': selectedTab === 'Action',
            'text-base text-steel-700': selectedTab !== 'Action',
            'flex-1 text-sm': !isDesktop,
          }"
          @click="handleTabSelection('Action')"
        >
          Action
          <span v-if="actionOffersLength && actionOffersLength !== 0">
            ({{ actionOffersLength }})
          </span>
        </span>

        <span
          class="min-w-max cursor-pointer"
          :class="{
            'border-b-2 border-sun-700 text-sun-700': selectedTab === 'Waiting',
            'text-base text-steel-700': selectedTab !== 'Waiting',
            'flex-1 text-sm': !isDesktop,
          }"
          @click="handleTabSelection('Waiting')"
        >
          Waiting
          <span v-if="waitingOffersLength && waitingOffersLength !== 0">
            ({{ waitingOffersLength }})
          </span>
        </span>

        <span
          class="cursor-pointer"
          :class="{
            'border-b-2 border-sun-700 text-sun-700': selectedTab === 'History',
            'text-base text-steel-700': selectedTab !== 'History',
            'flex-1 text-sm': !isDesktop,
          }"
          @click="handleTabSelection('History')"
        >
          History
        </span>
      </div>

      <p
        class="mr-4 pb-2 pl-4 pt-2 text-left font-normal text-steel-900 md:pt-1"
      >
        Got questions?
        <a
          href="https://service.highway.com/en/articles/13392157-how-to-manage-your-offers"
          target="_blank"
          class="cursor-pointer text-sun-700 underline"
        >
          Learn more about how offers work.
        </a>
      </p>
    </div>

    <div
      :class="{
        'mx-4': isDesktop,
      }"
    >
      <div
        v-if="combinedLoading"
        class="flex h-[calc(100vh-250px)] w-full items-center justify-center rounded-md border border-lighter-gray p-5"
        :class="{
          'shadow-md': isDesktop,
          '': !isDesktop,
        }"
      >
        <div>
          <img
            class="loading m-auto h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading spinner"
          />
          <p class="mt-2 text-base text-steel-900">Loading...</p>
        </div>
      </div>

      <DataTable
        v-else-if="isDesktop"
        class="offers-table !h-[calc(100vh-250px)] shadow-md"
        :data="offers"
        :show-pagination="true"
        :header-grid-class="gridColumnTemplate"
        :row-grid-class="gridColumnTemplate"
        :show-total-results-text="offers?.length > 0"
        :custom-results-text="resultsText"
      >
        <template
          #header
          v-if="selectedTab === 'Action'"
        >
          <div class="col-span-4 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">Load ID</p>
          </div>
          <div class="col-span-4 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">Origin</p>
          </div>
          <div class="col-span-4 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">Destination</p>
          </div>
          <div class="col-span-2 flex items-center px-1 py-2">
            <p class="text-sm font-medium text-steel-1100">Equipment</p>
          </div>
          <div class="col-span-2 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">Miles</p>
          </div>
          <div class="col-span-3 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">Received</p>
          </div>
          <div class="col-span-3 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">Amount</p>
          </div>
          <div class="col-span-8 flex items-center py-2 pr-3">
            <p class="text-sm font-medium text-steel-1100">RPM</p>
          </div>
        </template>

        <template
          #header
          v-else
        >
          <div class="col-span-3 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">Load ID</p>
          </div>
          <div class="col-span-3 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">Origin</p>
          </div>
          <div class="col-span-3 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">Destination</p>
          </div>
          <div class="col-span-2 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">
              {{ selectedTab === 'Waiting' ? 'My Offer' : 'Booked Rate' }}
            </p>
          </div>
          <div class="col-span-1 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">RPM</p>
          </div>
          <div class="col-span-4 flex items-center px-3 py-2">
            <p class="text-sm font-medium text-steel-1100">
              {{ selectedTab === 'Waiting' ? 'Sent' : 'Closed' }}
            </p>
          </div>
        </template>

        <template
          #row="{ item, index }"
          v-if="selectedTab === 'Action'"
        >
          <CollapsibleComponent
            class="offers-collapsible col-span-30"
            title-class="ml-2"
            :is-expanded="index === 0"
            @mouseenter="handleSelectCompany(item)"
          >
            <template #title>
              <div class="-ml-2 flex items-center gap-2 py-4">
                <img
                  class="h-6 text-2xs"
                  :src="item?.broker_logo?.url"
                  :alt="item.company_name"
                />
                <p
                  v-if="item.offers?.length > 0"
                  class="text-sm text-steel-700"
                >
                  ({{ item.offer_count }})
                </p>
              </div>
            </template>

            <template #content>
              <div
                v-for="(offer, offerIndex) in item.offers"
                :key="offer.id"
                class="grid cursor-pointer grid-cols-30 border-b border-steel-300 bg-steel-100 hover:bg-steel-200"
                :class="{
                  'border-t': !offerIndex,
                  '!bg-sun-50': selectedOffer?.id === offer.id,
                }"
                @click="handleSelectedOffer(offer)"
              >
                <div class="col-span-4 flex items-center px-3 py-3">
                  <p
                    class="whitespace-normal break-all pr-3 text-left text-sm text-steel-900 underline"
                  >
                    {{ offer?.load?.external_order_or_load_number }}
                  </p>
                </div>

                <div
                  class="col-span-4 flex flex-col items-start justify-center gap-1 px-3 py-3"
                >
                  <p
                    v-if="offer?.load?.stops?.length > 0"
                    class="text-left text-sm font-normal capitalize text-steel-900"
                  >
                    {{ offer?.load?.stops?.[0]?.city?.toLowerCase()
                    }}{{
                      offer?.load?.stops?.[0]?.state
                        ? `, ${offer?.load?.stops?.[0]?.state}`
                        : ''
                    }}
                  </p>
                  <p
                    v-else
                    class="text-left text-sm font-normal text-steel-800"
                  >
                    -
                  </p>
                  <p class="text-xs font-light text-steel-800">
                    {{
                      formatStopDate(
                        offer?.load?.pickup_time,
                        offer?.load?.pickup_time_timezone,
                        offer?.load?.pickup_has_time
                      )
                    }}
                  </p>
                </div>

                <div
                  class="col-span-4 flex flex-col items-start justify-center gap-1 px-3 py-3"
                >
                  <p
                    v-if="offer?.load?.stops?.length > 0"
                    class="text-left text-sm font-normal capitalize text-steel-900"
                  >
                    {{
                      offer?.load?.stops?.[
                        offer?.load?.stops?.length - 1
                      ]?.city?.toLowerCase()
                    }}{{
                      offer?.load?.stops?.[offer?.load?.stops?.length - 1]
                        ?.state
                        ? `, ${offer?.load?.stops?.[offer?.load?.stops?.length - 1]?.state}`
                        : ''
                    }}
                  </p>
                  <p
                    v-else
                    class="text-left text-sm font-normal text-steel-800"
                  >
                    -
                  </p>
                  <p class="text-xs font-light text-steel-800">
                    {{
                      formatStopDate(
                        offer?.load?.dropoff_time,
                        offer?.load?.dropoff_time_timezone,
                        offer?.load?.dropoff_has_time
                      )
                    }}
                  </p>
                </div>

                <div
                  class="col-span-2 flex flex-col items-start justify-center gap-1 px-1 py-3"
                >
                  <p
                    v-for="(type, typeIndex) in offer?.load
                      ?.posted_equipment_types"
                    :key="type"
                    class="text-left text-xs font-normal capitalize text-steel-900"
                  >
                    {{ type?.replace(/_/g, ' ')
                    }}{{
                      typeIndex !==
                      offer?.load?.posted_equipment_types?.length - 1
                        ? ', '
                        : ''
                    }}
                  </p>
                </div>

                <div
                  class="col-span-2 flex flex-col items-start justify-center gap-1 px-3 py-3"
                >
                  <p class="text-left text-xs font-normal text-steel-900">
                    {{ offer?.load?.posted_miles }} mi
                  </p>
                </div>

                <div
                  class="col-span-3 flex flex-col items-start justify-center gap-1 px-3 py-3"
                >
                  <p class="text-left text-xs font-normal text-steel-900">
                    {{
                      getTimeFromNow(
                        offer?.negotiation?.updated_at || offer.updated_at
                      )
                    }}
                  </p>
                </div>

                <div
                  class="col-span-3 flex flex-col items-end justify-center gap-1 py-3 pr-1"
                >
                  <p class="text-xl font-semibold">
                    {{ formatPrice(offer.rate, 0) }}
                  </p>
                  <p class="min-w-max text-xs font-normal text-steel-900">
                    Against your
                    {{
                      formatPrice(
                        offer?.negotiation?.offer_rates?.[
                          offer?.negotiation?.offer_rates?.length - 2
                        ]?.rate
                      )
                    }}
                  </p>
                </div>

                <div
                  class="col-span-8 flex items-center justify-between pl-[2px] pr-2"
                >
                  <p
                    v-if="formatPrice(offer?.load?.rate_per_mile) && offer.rate"
                    class="min-w-max text-xs font-normal text-steel-800"
                  >
                    {{
                      formatPrice(offer?.rate / offer?.load?.posted_miles)
                    }}/mi
                  </p>

                  <div class="flex gap-1">
                    <ButtonComponent
                      v-if="offer?.status === 'pending'"
                      variant="transparent"
                      class="border-none"
                      @click.stop="toggleRejectBottomSheet(offer)"
                    >
                      Reject
                    </ButtonComponent>
                    <ButtonComponent
                      v-if="canCounterOffer(offer)"
                      class="border border-steel-300"
                      @click.stop="handleCounter(offer)"
                    >
                      Counter
                    </ButtonComponent>
                    <ButtonComponent
                      variant="secondary"
                      class="!min-w-[85px]"
                      :is-loading="acceptedLoadingId === offer?.id"
                      @click.stop="handleAccept(offer)"
                    >
                      Accept
                    </ButtonComponent>
                  </div>
                </div>
              </div>
            </template>
          </CollapsibleComponent>
        </template>

        <template
          #row="{ item, index }"
          v-else
        >
          <CollapsibleComponent
            class="offers-collapsible col-span-16"
            title-class="ml-2 [&>*:first-child]:w-full"
            :is-expanded="
              initialOfferCompanyId === item?.company_id || index === 0
            "
            @mouseenter="handleSelectCompany(item)"
          >
            <template #title>
              <div class="flex w-full items-center justify-between">
                <div class="relative -ml-2 flex items-center gap-2 py-4">
                  <div
                    v-if="!imagesLoaded[item.company_name]"
                    class="absolute -ml-2 flex items-center gap-2"
                  >
                    <div
                      class="h-6 w-[60px] animate-pulse rounded-md bg-gray-200"
                    ></div>
                  </div>

                  <img
                    :src="item?.broker_logo?.url"
                    :alt="item.company_name"
                    class="h-6 text-2xs"
                    @error="imagesLoaded[item.company_name] = true"
                    @load="imagesLoaded[item.company_name] = true"
                  />
                  <p
                    v-if="imagesLoaded[item.company_name]"
                    class="text-sm text-steel-700"
                  >
                    ({{ item.offer_count }})
                  </p>
                </div>

                <p
                  v-if="
                    item?.book_it_now_type === 'offers_only_static' ||
                    item?.book_it_now_type === 'offers_and_book_it_now_static'
                  "
                  class="mr-6 font-normal text-steel-800"
                >
                  This broker's system cannot send replies to Highway.

                  <a
                    href="https://service.highway.com/en/articles/13392157-how-to-manage-your-offers#h_15d4a7c891"
                    target="_blank"
                    class="cursor-pointer pl-2 text-sm text-sun-700 underline"
                  >
                    Learn More
                  </a>
                </p>
              </div>
            </template>

            <template #content>
              <div>
                <div
                  v-for="(offer, offerIndex) in item.offers"
                  :key="offer.id"
                  class="grid cursor-pointer grid-cols-16 border-b border-steel-300 bg-steel-100 hover:bg-steel-200"
                  :class="{
                    'border-t': !offerIndex,
                    '!bg-sun-50': selectedOffer?.id === offer.id,
                  }"
                  @click="handleSelectedOffer(offer)"
                >
                  <div class="col-span-3 flex items-center px-3 py-3">
                    <p
                      class="whitespace-normal break-all pr-3 text-left text-sm text-steel-900 underline"
                    >
                      {{ offer?.load?.external_order_or_load_number }}
                    </p>
                  </div>

                  <div
                    class="col-span-3 flex flex-col items-start gap-1 px-3 py-3"
                  >
                    <p
                      v-if="offer?.load?.stops?.length > 0"
                      class="text-left text-sm font-normal capitalize text-steel-900"
                    >
                      {{ offer?.load?.stops?.[0]?.city?.toLowerCase()
                      }}{{
                        offer?.load?.stops?.[0]?.state
                          ? `, ${offer?.load?.stops?.[0]?.state}`
                          : ''
                      }}
                    </p>
                    <p
                      v-else
                      class="text-left text-sm font-normal text-steel-800"
                    >
                      -
                    </p>
                    <p class="text-xs font-light text-steel-800">
                      {{
                        formatStopDate(
                          offer?.load?.pickup_time,
                          offer?.load?.pickup_time_timezone,
                          offer?.load?.pickup_has_time
                        )
                      }}
                    </p>
                  </div>

                  <div
                    class="col-span-3 flex flex-col items-start gap-1 px-3 py-3"
                  >
                    <p
                      v-if="offer?.load?.stops?.length > 0"
                      class="text-left text-sm font-normal capitalize text-steel-900"
                    >
                      {{
                        offer?.load?.stops?.[
                          offer?.load?.stops?.length - 1
                        ]?.city?.toLowerCase()
                      }}{{
                        offer?.load?.stops?.[offer?.load?.stops?.length - 1]
                          ?.state
                          ? `, ${offer?.load?.stops?.[offer?.load?.stops?.length - 1]?.state}`
                          : ''
                      }}
                    </p>
                    <p
                      v-else
                      class="text-left text-sm font-normal text-steel-800"
                    >
                      -
                    </p>
                    <p class="text-xs font-light text-steel-800">
                      {{
                        formatStopDate(
                          offer?.load?.dropoff_time,
                          offer?.load?.dropoff_time_timezone,
                          offer?.load?.dropoff_has_time
                        )
                      }}
                    </p>
                  </div>

                  <div
                    class="col-span-2 flex flex-col items-end justify-center gap-1 py-3 pr-2"
                  >
                    <p
                      v-if="offer.rate"
                      class="text-xl font-semibold"
                    >
                      {{ formatPrice(offer.rate, 0) }}
                    </p>
                    <p
                      v-else
                      class="text-xl font-semibold"
                    >
                      -
                    </p>

                    <p
                      v-if="
                        offer.negotiation?.current_step === 1 &&
                        selectedTab === 'Waiting'
                      "
                      class="min-w-max text-xs font-normal text-steel-900"
                    >
                      Initial Broker Price:
                      {{ formatPrice(offer.load?.price, 0) }}
                    </p>
                    <p
                      v-else-if="selectedTab === 'Waiting'"
                      class="min-w-max text-xs font-normal text-steel-900"
                    >
                      Countering:
                      {{
                        formatPrice(
                          offer?.negotiation?.current_offer_rate?.rate
                        )
                      }}
                    </p>
                  </div>

                  <div class="col-span-1 flex items-center gap-1 py-3 pl-2">
                    <p
                      v-if="
                        formatPrice(offer?.load?.rate_per_mile) && offer.rate
                      "
                      class="min-w-max text-xs font-normal text-steel-800"
                    >
                      {{ formatPrice(offer?.rate_per_mile) }}/mi
                    </p>
                  </div>

                  <div
                    v-if="selectedTab === 'Waiting'"
                    class="col-span-4 flex items-center justify-between gap-2 py-3 pl-1 pr-4"
                  >
                    <div class="flex flex-col gap-1">
                      <p class="min-w-max text-xs font-normal text-steel-800">
                        {{ getTimeFromNow(offer.created_at) }}
                      </p>
                    </div>

                    <div class="flex items-center gap-2">
                      <ButtonComponent @click.stop="handleWithdraw(offer)">
                        Withdraw
                      </ButtonComponent>
                      <ButtonComponent
                        variant="transparent"
                        @click.stop="handleEdit(offer)"
                      >
                        Edit
                      </ButtonComponent>
                    </div>
                  </div>
                  <div
                    v-else
                    class="col-span-4 flex items-center justify-between gap-2 p-3 pr-4"
                  >
                    <div class="flex flex-col gap-1">
                      <p class="min-w-max text-xs font-normal text-steel-800">
                        {{
                          dayjs(offer.closed_at || updated_at).format(
                            'MM/DD/YYYY'
                          )
                        }}
                      </p>

                      <span
                        v-if="offer.status === 'accepted'"
                        class="rounded-full bg-forest-200 px-2 py-0.5 text-xs text-forest-900"
                      >
                        Awarded
                      </span>
                      <span
                        v-if="offer.status === 'booked'"
                        class="w-min rounded-full bg-ocean-200 px-2 py-0.5 text-xs text-ocean-600"
                      >
                        Booked
                      </span>
                      <span
                        v-else-if="
                          offer.status === 'rejected' &&
                          offer?.rejected_by?.company_type_name === 'carrier'
                        "
                        class="rounded-full bg-steel-200 px-2 py-0.5 text-xs text-steel-800"
                      >
                        Withdrawn
                      </span>
                      <span
                        v-else-if="offer.status === 'rejected'"
                        class="w-min rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-600"
                      >
                        Rejected
                      </span>
                      <span
                        v-else-if="offer.status === 'expired'"
                        class="w-min rounded-full bg-pumpkin-100 px-2 py-0.5 text-xs text-pumpkin-900"
                      >
                        Expired
                      </span>
                    </div>

                    <ButtonComponent class="border border-steel-300">
                      History
                    </ButtonComponent>
                  </div>
                </div>

                <div v-if="offer_company_ids_loading[item?.company_id]">
                  <img
                    class="loading m-auto mb-4 h-12 w-12"
                    src="@/assets/loading-v2-yellow.svg"
                    alt="loading spinner"
                  />
                </div>

                <div v-if="displayLoadMoreOffer(item)">
                  <InfiniteScroll
                    @load-more="loadMoreSingleBrokerOffer(item)"
                  ></InfiniteScroll>
                </div>
              </div>
            </template>
          </CollapsibleComponent>
        </template>

        <template #emptyState>
          <div class="flex h-full items-center justify-center">
            <p class="text-lg font-normal text-steel-700">{{ empty_state }}</p>
          </div>
        </template>
      </DataTable>

      <!-- mobile view -->
      <div v-else>
        <div
          v-if="is_offers_loading"
          class="min-h-[500px] items-center px-2"
        >
          <img
            class="loading m-auto my-8 h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading"
          />
        </div>

        <div
          v-else-if="offers?.length > 0"
          class="mb-8 flex flex-wrap justify-center gap-4"
        >
          <div class="flex w-full flex-col items-center">
            <CollapsibleComponent
              v-for="(item, index) in offers"
              :key="item.id"
              :is-expanded="index === 0"
              class="offers-collapsible w-full"
              title-class="pl-2 border-y border-steel-300 bg-white"
              @pointerdown="handleSelectCompany(item)"
            >
              <template #title>
                <div class="-ml-2 flex items-center gap-2 py-4">
                  <img
                    class="h-6 text-2xs"
                    :src="item?.broker_logo?.url"
                    :alt="item.company_name"
                  />
                  <p class="text-base text-steel-700">
                    ({{ item.offer_count }})
                  </p>
                </div>
              </template>

              <template #content>
                <div v-if="offer_company_ids_loading[item?.company_id]">
                  <img
                    class="loading m-auto my-4 h-12 w-12"
                    src="@/assets/loading-v2-yellow.svg"
                    alt="loading spinner"
                  />
                </div>

                <div v-else>
                  <div
                    v-for="offer in item?.offers"
                    :key="offer.id"
                    class="relative mx-2 mx-auto my-4 flex w-full max-w-[400px] flex-col gap-1 rounded-lg border border-steel-200 border-steel-300 bg-white px-3 pb-4 text-left shadow-md"
                    @click="handleSelectedOffer(offer)"
                  >
                    <div class="flex-between">
                      <p
                        class="max-w-[200px] whitespace-normal break-all pr-3 text-left text-base text-steel-900"
                      >
                        {{ offer?.load?.external_order_or_load_number }}
                      </p>

                      <div class="flex flex-col items-end py-3">
                        <p class="text-2xl font-bold">
                          {{ formatPrice(offer.rate, 0) }}
                        </p>

                        <p
                          v-if="
                            formatPrice(offer?.load?.rate_per_mile) &&
                            offer.rate
                          "
                          class="text-xs font-normal text-steel-700"
                        >
                          {{ formatPrice(offer?.rate_per_mile) }}/mi
                        </p>
                      </div>
                    </div>

                    <div class="flex items-center justify-between">
                      <span
                        v-if="offer.status === 'accepted'"
                        class="rounded-full bg-ocean-200 px-2 py-0.5 text-xs text-ocean-600"
                      >
                        Awarded
                      </span>
                      <span
                        v-if="offer.status === 'booked'"
                        class="rounded-full bg-ocean-200 px-2 py-0.5 text-xs text-ocean-600"
                      >
                        Booked
                      </span>
                      <span
                        v-else-if="
                          offer.status === 'rejected' &&
                          offer?.rejected_by?.company_type_name === 'carrier'
                        "
                        class="rounded-full bg-steel-200 px-2 py-0.5 text-xs text-steel-800"
                      >
                        Withdrawn
                      </span>
                      <span
                        v-else-if="offer.status === 'rejected'"
                        class="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-500"
                      >
                        Rejected
                      </span>

                      <p
                        v-if="offer.status"
                        class="ml-auto min-w-max text-sm font-normal text-steel-800"
                      >
                        {{ dayjs(offer.updated_at).format('MM/DD/YYYY') }}
                      </p>
                    </div>

                    <div class="relative flex flex-col gap-1">
                      <div class="flex gap-2">
                        <div class="ml-3 flex flex-col flex-wrap items-start">
                          <p
                            class="mr-3 min-w-fit text-left text-base font-medium capitalize"
                          >
                            {{ formatLocation(offer?.load?.stops?.[0]) }}
                          </p>

                          <p
                            class="text-left text-xs font-normal text-steel-700"
                          >
                            {{
                              formatStopDate(
                                offer?.load?.pickup_time,
                                offer?.load?.pickup_time_timezone,
                                offer?.load?.pickup_has_time
                              )
                            }}
                          </p>
                        </div>
                      </div>

                      <img
                        class="absolute -left-1 top-[12px] -ml-0.5 h-12 w-3"
                        src="@/assets/loads/line-yellow.svg"
                        alt="yellow icon"
                      />

                      <div class="flex gap-2">
                        <div class="ml-3 flex flex-col flex-wrap items-start">
                          <p
                            class="mr-3 min-w-fit text-left text-base font-medium capitalize"
                          >
                            {{
                              formatLocation(
                                offer?.load?.stops?.[
                                  offer?.load?.stops?.length - 1
                                ]
                              )
                            }}
                          </p>

                          <p class="text-xs font-normal text-steel-700">
                            {{
                              formatStopDate(
                                offer?.load?.dropoff_time,
                                offer?.load?.dropoff_time_timezone,
                                offer?.load?.dropoff_has_time
                              )
                            }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="selectedTab === 'Action'"
                      class="flex flex-col gap-2 [&>*]:flex-1"
                    >
                      <ButtonComponent
                        v-if="offer?.status === 'pending'"
                        variant="transparent"
                        class="border-none"
                        @click.stop="toggleRejectBottomSheet(offer)"
                      >
                        Reject
                      </ButtonComponent>

                      <div class="flex gap-2 [&>*]:flex-1">
                        <ButtonComponent
                          v-if="
                            (offer?.load?.book_it_now_type === 'offers_only' ||
                              offer?.load?.book_it_now_type ===
                                'offers_and_book_it_now') &&
                            offer?.status === 'pending'
                          "
                          class="border border-steel-300"
                          @click.stop="handleCounter(offer)"
                        >
                          Counter
                        </ButtonComponent>
                        <ButtonComponent
                          variant="secondary"
                          :is-loading="acceptedLoadingId === offer?.id"
                          @click.stop="handleAccept(offer)"
                        >
                          Accept
                        </ButtonComponent>
                      </div>
                    </div>

                    <div
                      v-else-if="selectedTab === 'Waiting'"
                      class="mt-2 flex gap-2 [&>*]:flex-1"
                    >
                      <ButtonComponent
                        class="border border-steel-300"
                        @click.stop="handleWithdraw(offer)"
                      >
                        Withdraw
                      </ButtonComponent>
                      <ButtonComponent
                        class="border border-steel-300"
                        @click.stop="handleEdit(offer)"
                      >
                        Edit
                      </ButtonComponent>
                    </div>

                    <div
                      v-else
                      class="mt-2 flex gap-2 [&>*]:flex-1"
                    >
                      <ButtonComponent class="border border-steel-300">
                        History
                      </ButtonComponent>
                    </div>
                  </div>
                </div>
              </template>
            </CollapsibleComponent>
          </div>
        </div>

        <InfiniteScroll @load-more="loadMoreOffers"></InfiniteScroll>

        <div
          v-if="isOffersRefetching && !is_offers_loading"
          class="pb-12 pt-6"
        >
          <img
            class="loading m-auto h-12 w-12"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading spinner"
          />
        </div>
        <div
          v-else-if="offers?.length === 0 && !is_offers_loading"
          class="mt-4 flex flex-col items-center gap-3"
        >
          <img
            src="@/assets/offers-icon.svg"
            alt="offers"
          />
          <p class="text-lg text-steel-700">{{ empty_state }}</p>
        </div>
      </div>
    </div>

    <OffersDrawer
      v-if="showOffersDrawer"
      v-model="showOffersDrawer"
      :offer="selectedOffer"
      :counter-input="counterInput"
      :accept-loading="acceptedLoadingId"
      :initial-counter="intialCounterValue"
      :selected-tab="selectedTab"
      @handle-counter="handleCounter"
      @handle-accept="handleAccept"
      @handle-reject="toggleRejectBottomSheet"
      @handle-withdraw="handleWithdraw"
      @handle-edit="handleEdit"
      @handle-update-selected-offer="handleUpdateSelectedOffer"
      @close-drawer="handleCloseDrawer"
    ></OffersDrawer>

    <RemoveBottomSheet
      submit-text="Reject"
      :is-loading="rejectedLoadingId === selectedOffer?.id"
      :visible="removeBottomSheet"
      @close="toggleRejectBottomSheet"
      @submit="handleReject"
    >
      <template #title>
        <p class="text-center">
          Are you sure you want to reject offer
          <span class="font-semibold">
            {{ selectedOffer?.load?.external_order_or_load_number }}
          </span>
          ?
        </p>
      </template>
    </RemoveBottomSheet>

    <WithdrawOfferBottomSheet
      :visible="showWithDrawBottomSheet"
      :offer="selectedOffer"
      @cancel="toggleWithDrawBottomSheet"
      @close="toggleWithDrawBottomSheet"
    ></WithdrawOfferBottomSheet>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeMount } from 'vue';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import { useToast } from 'vue-toast-notification';
import { useRouter } from 'vue-router';
import {
  ButtonComponent,
  DataTable,
  InfiniteScroll,
  CollapsibleComponent,
  OffersDrawer,
  RemoveBottomSheet,
  WithdrawOfferBottomSheet,
} from '@/components';
import { formatPrice, formatStopDate, getTimeFromNow } from '@/utils';

const store = useStore();
const toast = useToast();
const router = useRouter();

defineProps({
  isDesktop: {
    type: Boolean,
    default: false,
  },
});

const imagesLoaded = ref({});

const selectedTab = ref('Action');

const handleTabSelection = (tab) => {
  selectedTab.value = tab;
};

const offers = computed(() => {
  if (selectedTab?.value === 'Action') {
    return store.state.loads?.action_offers;
  } else if (selectedTab.value === 'Waiting') {
    return store.state.loads?.waiting_offers;
  } else {
    return store.state.loads?.history_offers;
  }
});

const actionOffersLength = computed(() => {
  const length = store.state.loads?.action_offers?.reduce(
    (acc, curr) => acc + curr.offer_count,
    0
  );

  if (length > 99) {
    return '99+';
  }
  return length;
});

const waitingOffersLength = computed(() => {
  const length = store.state.loads?.waiting_offers?.reduce(
    (acc, curr) => acc + curr.offer_count,
    0
  );

  if (length > 99) {
    return '99+';
  }
  return length;
});

const getCompanyById = (company_id, type) => {
  store.dispatch('loads/getOffersByCompanyId', {
    company_id,
    type,
  });
};

const is_offers_loading = computed(
  () => store.state.loads?.is_offers_by_company_id_loading
);

const offer_company_ids_loading = computed(
  () => store.state.loads?.offer_company_ids_loading
);

const initialOfferCompanyId = computed(
  () => store.state.loads?.initial_offer_company_id
);

const initialLoading = ref(false);
const combinedLoading = computed(
  () => is_offers_loading?.value || initialLoading?.value
);

const initialView = ref({
  Action: false,
  Waiting: true,
  History: true,
});

const selectedOffer = ref(null);
const initialOffer = ref('');

onBeforeMount(() => {
  const existingHash = router.currentRoute.value?.hash;
  if (!existingHash) return;

  const [tab, offer] = existingHash
    .slice(1)
    .replace(/^./, (c) => c.toUpperCase())
    .split('-');

  selectedTab.value = tab;

  if (offer) {
    initialOffer.value = offer;
  }
});

watch(selectedTab, (newValue) => {
  router.push({
    hash: `#${newValue?.toLowerCase()}`,
  });

  if (initialView?.value?.[newValue]) {
    initialLoading.value = true;
    initialView.value[newValue] = false;

    setTimeout(() => {
      initialLoading.value = false;
    }, 500);
  }

  if (newValue) {
    const initialCompany = offers?.value?.[0];

    if (
      initialCompany?.company_id &&
      !Object.prototype.hasOwnProperty.call(initialCompany, 'offers')
    ) {
      getCompanyById(initialCompany?.company_id, newValue?.toLowerCase());
    }
  }
});

const handleSelectCompany = (item) => {
  if (!Object.prototype.hasOwnProperty.call(item, 'offers')) {
    getCompanyById(item?.company_id, selectedTab?.value?.toLowerCase());
  }
};

const gridColumnTemplate = computed(() => {
  if (selectedTab.value === 'Action') {
    return 'grid-cols-30';
  }

  return 'grid-cols-16';
});

const offers_pagination = computed(() => {
  if (selectedTab?.value === 'Action') {
    return store.state.loads?.action_offers_main_pagination;
  } else if (selectedTab.value === 'Waiting') {
    return store.state.loads?.waiting_offers_main_pagination;
  } else {
    return store.state.loads?.history_offers_main_pagination;
  }
});

const empty_state = computed(() => {
  if (selectedTab?.value === 'Action') {
    return 'You have no offers requiring action at this time.';
  } else if (selectedTab.value === 'Waiting') {
    return 'You have no offers awaiting a response at this time.';
  } else {
    return 'No offers history';
  }
});

const resultsText = computed(
  () =>
    `Showing ${offers?.value?.length} of ${offers_pagination?.value?.totalRecords} broker offers`
);

const isOffersRefetching = ref(false);

const loadMoreOffers = async () => {
  console.log('Hit infinite scroll');

  if (offers_pagination?.value?.totalRecords > offers?.value?.length) {
    isOffersRefetching.value = true;

    // fetch next page active offers
    // TODO -- different request here
    await store.dispatch('loads/getOffers', {
      page: offers_pagination?.value?.currentPage + 1,
      refetch: true,
    });

    isOffersRefetching.value = false;
  }
};

const displayLoadMoreOffer = (item) => {
  let pagination;
  if (selectedTab.value === 'Action') {
    pagination = 'action_offers_pagination';
  } else if (selectedTab.value === 'Waiting') {
    pagination = 'waiting_offers_pagination';
  } else {
    pagination = 'history_offers_pagination';
  }
  const offerPagination = store.state.loads?.[pagination]?.[item?.company_id];

  if (item?.offers?.length !== offerPagination?.totalRecords) {
    return offerPagination;
  } else {
    return false;
  }
};

const loadMoreSingleBrokerOffer = async (offer) => {
  console.log('Hit infinite scroll');

  const pagination = displayLoadMoreOffer(offer);

  if (!offer_company_ids_loading?.value?.[offer?.company_id]) {
    await store.dispatch('loads/getOffersByCompanyId', {
      company_id: offer?.company_id,
      type: selectedTab?.value?.toLowerCase(),
      refetch: true,
      page: pagination?.currentPage + 1,
    });
  }
};

// const onSelectPage = (page) => {
//   store.dispatch('loads/getOffers', {
//     page,
//   });
// };

const canCounterOffer = (offer) => {
  const correctType =
    offer?.load?.book_it_now_type === 'offers_only' ||
    offer?.load?.book_it_now_type === 'offers_and_book_it_now';

  const nextStep = offer?.negotiation?.next_step;
  const correctStep =
    nextStep?.direction === 'carrier' && nextStep?.rate_type === 'counter';

  return correctType && correctStep && offer?.status === 'pending';
};

const showOffersDrawer = ref(false);
const intialCounterValue = ref('');

const handleCloseDrawer = () => {
  showOffersDrawer.value = false;
};

const counterInput = ref(false);
const handleCounter = (offer) => {
  if (offer?.id) {
    showOffersDrawer.value = true;
    selectedOffer.value = offer;
  }
  counterInput.value = !counterInput.value;
};

watch(showOffersDrawer, (newValue) => {
  if (!newValue) {
    if (!removeBottomSheet.value) {
      selectedOffer.value = null;
    }
    intialCounterValue.value = '';

    counterInput.value = false;
  }
});

watch(
  offers,
  (newValue) => {
    if (newValue?.length > 0 && initialOffer?.value) {
      const findInitialOfferCompany = newValue?.find(
        (el) => el.company_id == initialOfferCompanyId?.value
      );

      let company;
      if (findInitialOfferCompany) {
        company = findInitialOfferCompany;
      } else {
        company = newValue?.[0];
      }

      const findInitialOffer = company?.offers?.find(
        (el) => el.id == initialOffer.value
      );

      if (findInitialOffer) {
        handleSelectedOffer(findInitialOffer);
      }
    }
  },
  { deep: true }
);

const handleSelectedOffer = (offer) => {
  if (!selectedOffer.value) {
    showOffersDrawer.value = true;
    selectedOffer.value = offer;
  } else {
    showOffersDrawer.value = false;
    selectedOffer.value = null;
  }
};

const removeBottomSheet = ref(false);
const rejectedLoadingId = ref('');

const toggleRejectBottomSheet = (offer) => {
  if (offer?.id) {
    selectedOffer.value = offer;
  } else {
    selectedOffer.value = {};
  }
  removeBottomSheet.value = !removeBottomSheet.value;
};

const handleReject = async () => {
  rejectedLoadingId.value = selectedOffer.value?.id;
  const broker_company_id = selectedOffer.value?.load?.broker_company_id;

  const res = await store.dispatch('loads/rejectOffer', {
    id: selectedOffer.value?.id,
  });

  if ([200, 201, 204].includes(res?.status) && res.data) {
    const findBrokerActionsOffers = store.state.loads?.action_offers?.find(
      (el) => el.company_id === broker_company_id
    );

    if (findBrokerActionsOffers?.offers?.length === 1) {
      await store.dispatch('loads/getOffersByType', {
        type: 'actions',
      });
    }

    await store.dispatch('loads/getOffersByCompanyId', {
      company_id: broker_company_id,
      type: 'actions',
    });

    await toast.open({
      message: 'Offer rejected',
      position: 'top-right',
      duration: 5000,
    });
  }

  selectedOffer.value = null;
  rejectedLoadingId.value = '';
  toggleRejectBottomSheet();
};

const acceptedLoadingId = ref('');

const handleAccept = async (offer) => {
  acceptedLoadingId.value = offer?.id;

  const company_id = offer?.load?.broker_company_id;

  const res = await store.dispatch('loads/acceptOffer', {
    id: offer?.id,
  });

  if ([200, 201, 204].includes(res?.status) && res.data) {
    const findBrokerActionsOffers = store.state.loads?.action_offers?.find(
      (el) => el.company_id === company_id
    );

    if (findBrokerActionsOffers?.offers?.length === 1) {
      await store.dispatch('loads/getOffersByType', {
        type: 'actions',
      });
    }

    await store.dispatch('loads/getOffersByCompanyId', {
      company_id,
      type: 'actions',
    });

    await toast.open({
      message: 'Offer accepted',
      position: 'top-right',
      duration: 5000,
    });

    await store.dispatch('loads/getOffersByType', {
      type: 'history',
    });
  } else {
    await toast.open({
      type: 'error',
      message: 'Unexpected error. Please try again.',
      position: 'top-right',
      duration: 5000,
    });
  }

  selectedOffer.value = null;
  acceptedLoadingId.value = '';
  handleCloseDrawer();
};

const showWithDrawBottomSheet = ref(false);
const toggleWithDrawBottomSheet = () => {
  showWithDrawBottomSheet.value = !showWithDrawBottomSheet.value;

  if (showOffersDrawer.value) {
    handleCloseDrawer();
  }
};
const handleWithdraw = (offer) => {
  selectedOffer.value = offer;
  toggleWithDrawBottomSheet();
};

const handleEdit = (offer) => {
  intialCounterValue.value = offer.rate;
  handleCounter(offer);
};

const handleUpdateSelectedOffer = (offer) => {
  selectedOffer.value = offer;
};

const formatLocation = (stop) => {
  const city = stop?.city || stop?.area_five_zip_city;
  const state = stop?.state || stop?.area_five_zip_state;

  return `${city?.toLowerCase()}${state ? ', ' + state : ''}`;
};
</script>

<style>
.offers-collapsible > button:first-child {
  @apply flex-row-reverse justify-end gap-2;
}
.offers-collapsible > div > div:first-child {
  @apply mx-2 md:mx-0;
}

.offers-table > div:nth-child(2) {
  @apply overflow-x-hidden;
}
</style>
